inherit("Modules/CarouselList", {
	parent: `Module`,
	defaults: {
		eventId: null, // used in the context of events, for referencing products / collections
		carousels: null, // required. Example below:
		/*
			[
				{
					ordinal: 1,
					title: `display name test`,
					blurb: `blurb test`,
					layout: `SINGLE_ROW`,
					orientation: `PORTRAIT`,
					mobileLayout: `SINGLE_COLUMN`,
					mobileOrientation: `LANDSCAPE`,
					items: [
						{
							title: `Test`,
							blurb: `Test`,
							hoverMessage: `View`,
							type: `COLLECTION`,
							externalUrl: null,
							imageUrl: `http://localhost:8090/static/images/external/img/560b09df-a50a-41cd-9ebd-da01959bf5bf.png`,
							ordinal: 1,
							id: 2,
						}
					]
				}
			]
		*/
		lazyLoad: true, // whether to render carousels as the user scrolls
		mode: `legacy`, // legacy | ux3
		showAnchorLinks: false, // whether to display the list of nav links
		render: true,
	},
	prepare(setup) {
		const { System, data } = this;
		const { carousels, eventId, lazyLoad, mode } = setup;
		const system = System.get();
		const { isiOS, isNativeApp } = system;
		const isiOSApp = isiOS && isNativeApp;
		let { showAnchorLinks } = setup;

		if (showAnchorLinks) {
			const carouselWithTitle = this.find(carousels, (carousel) => this.isString(carousel.title, 1));

			if (!this.isObject(carouselWithTitle)) {
				showAnchorLinks = false;
			}
		}

		this.assign(data, {
			carousels: this.filter(carousels, (carousel) => this.isArray(carousel.items, 1)),
			eventId,
			isiOSApp,
			mode,
			moreCarouselsAvailable: true,
			lazyLoad,
			numberPerPage: 3,
			pageIndex: 0,
			showAnchorLinks,
		});
	},
	constructor(setup) {
		const { DOM, data } = this;
		const { carousels, isiOSApp, showAnchorLinks } = data;

		this.renderCarousels();

		if (!showAnchorLinks || carousels.length < 2) {
			const navWrapper = this.get(`.links-wrapper`);
			DOM.remove(navWrapper);
		}

		if (isiOSApp) {
			this.addClass(`ios-app`);
		}
	},

	ready(setup) {
		const { App, DOM, data, elements, instances } = this;
		const { showAnchorLinks } = data;
		const carouselWrappers = this.select(`.carousel-wrapper`);

		if (!showAnchorLinks || carouselWrappers.length < 2) {
			return;
		}

		// add scroll event listener to render more carousels when the last one is in view
		App.onScroll(this.onScroll);

		this.defer({
			then: this.onScroll,
			duration: 100,
		});

		// create an observer to automatically toggle the .stickied class
		const { IntersectionObserver } = window;
		const handler = ([e]) => e.target.classList.toggle(`stickied`, e.intersectionRatio < 1);
		const options = { threshold: [1], rootMargin: `0px 1000px 1000px 1000px` };
		const observer = new IntersectionObserver(handler, options);

		const navWrapper = this.get(`.links-wrapper`);
		const navDimensions = DOM.getDimensions(navWrapper);
		const linksWrapper = this.get(`.links`);
		const carousels = this.map(carouselWrappers, (wrapper) => {
			const index = wrapper.getAttribute(`data-carousel-index`);
			const navLink = this.get(`[action="scroll"][data-carousel-index="${index}"]`);
			const dimensions = DOM.getDimensions(wrapper);

			return { dimensions, navLink };
		});

		if (this.isElement(navWrapper)) {
			observer.observe(navWrapper);

			elements.navWrapper = navWrapper;
			instances.observer = observer;

			App.onScroll((evt) => {
				const { height: navHeight, viewportTop: navViewportTop } = navDimensions;
				const navViewportPosition = navViewportTop + navHeight;
				const BUFFER = 20; // transition to a carousel slightly early
				let found = false;

				this.each(carousels, ({ dimensions, navLink }, index) => {
					if (found) {
						// top visible carousel already found
						this.removeClass(`selected`, navLink);
					} else {
						// still looking for top visible carousel
						const { height: wrapperHeight, viewportTop: wrapperViewportTop } = dimensions;
						const viewportPosition = wrapperViewportTop + wrapperHeight - BUFFER;

						if (viewportPosition < navViewportPosition) {
							if (navViewportPosition === 0 && index === 0) {
								this.addClass(`selected`, navLink);
								found = true;
							} else {
								this.removeClass(`selected`, navLink);
							}
						} else {
							if (!this.hasClass(`selected`, navLink)) {
								const computedStyles = window.getComputedStyle(linksWrapper);
								const paddingLeftStyle = computedStyles[`padding-left`];
								const paddingLeftString = paddingLeftStyle.replace(/[^0-9.]/, ``);
								const paddingLeft = parseFloat(paddingLeftString);
								const { offsetLeft: linkLeft } = navLink;
								const left = linkLeft - paddingLeft;
								const behavior = `smooth`;

								this.addClass(`selected`, navLink);
								linksWrapper.scrollTo({ behavior, left });
							}
							found = true;
						}
					}
				});
			}, true); // fire immediately
		}
	},

	api: {
		renderCarousels: {
			async value(options = {}) {
				const { data, App } = this;
				const { carousels, eventId, mode, numberPerPage, lazyLoad, pageIndex } = data;
				const { indexToRender } = options;
				const promises = [];
				const loadingKey = `renderingCarousels`;
				let startOfCarousels = pageIndex * numberPerPage;
				let endOfCarousels;

				if (!lazyLoad) {
					endOfCarousels = carousels.length;
				} else if (this.isFinite(indexToRender)) {
					endOfCarousels = Math.min(indexToRender + 1, carousels.length);
				} else {
					endOfCarousels = Math.min(startOfCarousels + numberPerPage, carousels.length);
				}

				App.block({ key: loadingKey });

				data.pageIndex = Math.ceil(endOfCarousels / numberPerPage);
				data.moreCarouselsAvailable = endOfCarousels < carousels.length;

				for (let i = startOfCarousels; i < endOfCarousels; i++) {
					window.console.log(`Rendering carousel ${i}`);
					const carousel = carousels[i];
					const name = `carousel-${i}`;
					const isScrollTrigger = i === endOfCarousels - 1;
					const { blurb, items, layout, mobileLayout, mobileOrientation, moreInfoHidden, orientation, title } =
						carousel;
					const promise = this.create({
						namespace: `Modules/Carousel`,
						setup: {
							blurb,
							eventId,
							items,
							layout,
							mobileLayout,
							mobileOrientation,
							mode,
							moreInfoHidden,
							name,
							orientation,
							title,
						},
					});

					if (isScrollTrigger) {
						const element = this.get(`[name="${name}"]`);
						this.addClass(`carousel-scroll-trigger`, element);
					}

					promises.push(promise);
				}

				await Promise.all(promises);

				if (this.isFinite(indexToRender)) {
					await this.defer({ duration: 600 }); // add a slight delay to ensure that the carousel is fully rendered
				}

				App.unblock({ key: loadingKey });

				return;
			},
		},

		onScroll: {
			value(evt) {
				const { App, DOM, data } = this;
				const { moreCarouselsAvailable } = data;

				if (!moreCarouselsAvailable) {
					App.offScroll(this.onScroll);
				}

				const cushion = 50;
				const target = this.get(`.carousel-scroll-trigger`);
				const inViewport = DOM.inViewport({ cushion, target });

				if (inViewport) {
					this.removeClass(`carousel-scroll-trigger`, target);
					this.renderCarousels();
				}
			},
		},
	},

	listeners: [
		{
			event: `click`,
			filter: `[action]`,
			async handler({ currentTarget: link }) {
				const { data, App, DOM, System } = this;
				const { numberPerPage, pageIndex } = data;
				const action = link.getAttribute(`action`);
				const { dataset } = link;

				switch (action) {
					case `scroll`: {
						const system = System.get();
						const { isNativeApp } = system;
						const { carouselIndex } = dataset;
						const index = this.parseInt(carouselIndex);
						const selector = `.carousel-wrapper[data-carousel-index="${index}"]`;
						const carouselWrapper = this.get(selector);

						const isIndexRendered = index < pageIndex * numberPerPage;

						if (!isIndexRendered) {
							await this.renderCarousels({ indexToRender: index });
						}

						const dimensions = DOM.getDimensions(carouselWrapper);
						const { top: carouselTop } = dimensions;
						// scroll up by a few extra pixels, to ensure that its link gets selected
						const offset = isNativeApp ? 68 : 8; // Height of header is 60px, plus 8px buffer
						const top = carouselTop + offset;
						const behavior = `smooth`;

						App.scrollTo({ behavior, top });
						break;
					}
				}
			},
		},
	],
	destructor(options) {
		const { elements, instances } = this;
		const { navWrapper } = elements;
		const { observer } = instances;

		if (this.isElement(navWrapper)) {
			observer.unobserve(navWrapper);
		}
	},
});

define("Modules/CarouselList.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Modules/CarouselList/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a href=\"#\" action=\"scroll\" data-carousel-index=\""
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "</a> ";
},"3":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"carousel-wrapper\" data-carousel-index=\""
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\"> <div name=\"carousel-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\"></div> </div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"links-wrapper\"> <div class=\"links\"> "
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"carousels") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":137},"end":{"line":1,"column":241}}})) != null ? stack1 : "")
    + " </div> </div> "
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"carousels") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":256},"end":{"line":1,"column":395}}})) != null ? stack1 : "");
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=CarouselList.js.map